"use client";

import { ArrowUpRight, ShieldCheck, FileText, Users, Scale, BookOpen, Gavel } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const labs = [{
  id: "POL-01",
  name: "Supervised Parenting Time",
  description: "Structured, neutral, and child-centered contact sessions designed to ensure safety and emotional well-being.",
  tags: ["#SAFETY", "#NEUTRALITY", "#CHILD-CENTERED"],
  accentVar: "--primary",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718652/img/supervised_parenting_time.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718652/img/supervised_parenting_time.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718652/img/supervised_parenting_time.webp-wvc-sizes",
  icon: ShieldCheck
}, {
  id: "POL-02",
  name: "Monitored Exchange",
  description: "Secure, professional hand-offs for children between parents, minimizing conflict and ensuring a calm transition.",
  tags: ["#CONFLICT-REDUCTION", "#STRUCTURED", "#SECURE"],
  accentVar: "--secondary",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718652/img/monitored_exchange.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718652/img/monitored_exchange.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718652/img/monitored_exchange.png-wvc-sizes",
  icon: Users
}, {
  id: "POL-03",
  name: "Transportation Services",
  description: "Reliable and safe transport for children and vulnerable individuals, maintaining strict professional boundaries.",
  tags: ["#RELIABLE", "#SAFE", "#PROFESSIONAL"],
  accentVar: "--accent",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718652/img/transportation_services.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718652/img/transportation_services.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718652/img/transportation_services.png-wvc-sizes",
  icon: FileText
}, {
  id: "POL-04",
  name: "Parenting Education",
  description: "Evidence-based guidance to support parents in developing healthy, child-focused communication and interaction skills.",
  tags: ["#EDUCATION", "#SKILL-BUILDING", "#SUPPORT"],
  accentVar: "--primary",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718652/img/parenting_education.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718652/img/parenting_education.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718652/img/parenting_education.png-wvc-sizes",
  icon: BookOpen
}, {
  id: "POL-05",
  name: "Therapeutic Supervision",
  description: "Specialized support for families navigating reunification or complex trauma-informed care requirements.",
  tags: ["#REUNIFICATION", "#TRAUMA-INFORMED", "#THERAPEUTIC"],
  accentVar: "--secondary",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718652/img/therapeutic_supervision.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718652/img/therapeutic_supervision.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718652/img/therapeutic_supervision.png-wvc-sizes",
  icon: Scale
}, {
  id: "POL-06",
  name: "Observation Reporting",
  description: "Detailed, objective, and neutral documentation provided for court review to assist in legal decision-making.",
  tags: ["#OBJECTIVE", "#COURT-READY", "#NEUTRAL"],
  accentVar: "--accent",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718652/img/observation_reporting.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718652/img/observation_reporting.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718652/img/observation_reporting.webp-wvc-sizes",
  icon: Gavel
}];
export default function Policyandlegalgrid() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: gridRef,
    isVisible: gridVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.05
  });
  return <section data-section-id="530" id="policyandlegalgrid" className="bg-background text-foreground relative overflow-hidden py-20 sm:py-28 lg:py-36">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-[0.04] mix-blend-overlay" style={{
      backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")"
    }} />

      <div className="relative mx-auto max-w-7xl px-6 lg:px-8">
        <div ref={headerRef} className={`grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-12 transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="lg:col-span-12">
            <span className="font-default text-xs sm:text-sm tracking-widest uppercase" style={{
            color: "var(--primary)"
          }}>
              Our Professional Services
            </span>
          </div>
          <div className="lg:col-span-10 lg:col-start-2">
            <h2 className="font-default font-semibold text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-[88px] leading-[1.05] tracking-tight text-foreground">
              Safe, structured,
              <br />
              <span className="text-muted-foreground">and neutral support.</span>
            </h2>
          </div>
        </div>

        <div ref={gridRef} className="mt-16 grid grid-cols-1 gap-px bg-border/40 md:grid-cols-2 lg:grid-cols-3">
          {labs.map((lab, i) => {
          const Icon = lab.icon;
          return <article key={lab.id} data-index={i} className={`group relative flex flex-col bg-background border border-border/40 transition-all duration-700 ease-out hover:border-transparent md:h-[480px] ${gridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
            transitionDelay: gridVisible ? `${i * 80}ms` : "0ms",
            "--lab-accent": `var(${lab.accentVar})`
          }} onMouseEnter={e => {
            e.currentTarget.style.borderColor = `var(${lab.accentVar})`;
          }} onMouseLeave={e => {
            e.currentTarget.style.borderColor = "";
          }}>
                <div className="relative h-48 sm:h-52 overflow-hidden">
                  <div className="absolute inset-0 transition-all duration-700 ease-out group-hover:opacity-100" style={{
                background: `linear-gradient(135deg, var(${lab.accentVar}) 0%, #2F3631 100%)`,
                opacity: 0.85
              }} />
                  <img src={lab.image} data-wvc-srcset={lab.srcset} data-wvc-sizes={lab.sizes} alt={lab.name} loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover mix-blend-luminosity grayscale opacity-60 transition-all duration-700 ease-out group-hover:grayscale-0 group-hover:opacity-90 group-hover:mix-blend-normal" />
                  <div className="absolute top-4 right-4">
                    <Icon className="h-5 w-5 text-white/70 transition-colors duration-500 group-hover:text-white" strokeWidth={1.5} />
                  </div>
                </div>

                <div className="relative flex flex-1 flex-col p-6 sm:p-7">
                  <div className="flex items-center justify-between">
                    <span className="font-default text-xs tracking-widest text-muted-foreground transition-colors duration-500">
                      [{lab.id}]
                    </span>
                    <span className="font-default text-xs text-muted-foreground/60">
                      /{String(i + 1).padStart(2, "0")}
                    </span>
                  </div>

                  <h3 className="font-default font-semibold mt-4 text-2xl sm:text-3xl leading-tight tracking-tight text-foreground">
                    {lab.name}
                  </h3>

                  <p className="mt-3 text-[15px] leading-relaxed text-muted-foreground">
                    {lab.description}
                  </p>

                  <ul className="mt-5 flex flex-wrap gap-2">
                    {lab.tags.map((tag, ti) => <li key={ti} data-index={ti} className="font-default text-[11px] tracking-wider text-muted-foreground border border-border/60 px-2 py-1 rounded-md transition-colors duration-300 group-hover:text-foreground group-hover:border-border">
                        {tag}
                      </li>)}
                  </ul>

                  <div className="mt-auto overflow-hidden pt-6">
                    <Link to="/OurServicesPage" className="font-default text-xs tracking-widest uppercase flex items-center gap-2 translate-y-8 opacity-0 transition-all duration-500 ease-out group-hover:translate-y-0 group-hover:opacity-100" style={{
                  color: `var(${lab.accentVar})`
                }}>
                      <span>LEARN MORE</span>
                      <ArrowUpRight className="h-4 w-4" strokeWidth={1.5} />
                    </Link>
                  </div>
                </div>

                <span aria-hidden="true" className="pointer-events-none absolute top-0 left-0 h-3 w-3 border-t border-l opacity-0 transition-opacity duration-500 group-hover:opacity-100" style={{
              borderColor: `var(${lab.accentVar})`
            }} />
                <span aria-hidden="true" className="pointer-events-none absolute bottom-0 right-0 h-3 w-3 border-b border-r opacity-0 transition-opacity duration-500 group-hover:opacity-100" style={{
              borderColor: `var(${lab.accentVar})`
            }} />
              </article>;
        })}
        </div>

        <div className="mt-16 flex justify-center">
          <Button asChild variant="ghost" className="font-default text-xs sm:text-sm tracking-widest uppercase text-foreground hover:bg-transparent hover:text-foreground group/btn px-0">
            <Link to="/OurServicesPage" className="inline-flex items-center gap-3">
              <span className="h-px w-8 bg-border transition-all duration-500 group-hover/btn:w-16" style={{
              backgroundColor: "var(--primary)"
            }} />
              <span>VIEW ALL SERVICES</span>
              <ArrowUpRight className="h-4 w-4 transition-transform duration-500 group-hover/btn:translate-x-1 group-hover/btn:-translate-y-1" strokeWidth={1.5} />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}